<script setup lang="ts">
import { onScopeDispose } from 'vue'
import { authContextEpoch, onAuthContextChanged } from '@/@vxapp/Auth/authContext'
import { useAuthStore } from '@/@vxapp/Auth/useAuthStore'
import { Action as A } from '@/@vxapp/@Enums/PHP/Auth/DataProviders/AbilityAction'
import { Subject as S } from '@/@vxapp/@Enums/PHP/Auth/Ability/Subjects/DataProviders/AbilitySubject'
import { nextTick, onMounted, ref, shallowRef, useTemplateRef, watch } from 'vue'
import type { PaginationQueryParams } from '@/@vxapp/@Enums/PaginationField'
import { PaginationField } from '@/@vxapp/@Enums/PaginationField'
import { PageMeta } from '@/@vxapp/@Enums/PageMeta'
import { ResponseCode } from '@/@vxapp/@Enums/PHP/HTTP/ResponseCode'
import { createLogger } from '@/@vxapp/@core/Logger/VxLog'
import { cloneReactiveData } from '@/@core/utils/cloneReactiveData'
import { useThemesStore } from '@/@vxapp/Messages/Themes/useThemesStore'
import ThemeDialog from '@/components/Dialogs/Themes/ThemeDialog.vue'
import ServiceListToolbar from '@/components/ServiceLists/Shared/ServiceListToolbar.vue'
import ThemesDataTable from '@/components/Tables/Messages/Themes/ThemesDataTable.vue'
import { DialogDisplayType } from '@/enums/dialog-display-type'

type ThemeData = Record<string, unknown>

definePage({ meta: { ...PageMeta.Themes.List.Meta, pageTitle: PageMeta.Themes.List.Title } })

const log = createLogger('ThemesListPage')
const route = useRoute()
const router = useRouter()
const themesStore = useThemesStore() as any
const themesTable = useTemplateRef<InstanceType<typeof ThemesDataTable>>('themesTable')
const themeData = ref<ThemeData | null>(null)
const isDialogVisible = shallowRef(false)
const isRouteReady = shallowRef(false)
const resultCount = shallowRef(0)
const defaults: PaginationQueryParams = { ...PaginationField.QueryParams, sort: '+id' }
const limitItems = [10, 25, 50, 100]

const sortItems = [
  { title: 'Oldest first', value: '+id' },
  { title: 'Newest first', value: '-id' },
]

function stringValue(value: unknown) {
  const normalized = Array.isArray(value) ? value[0] : value

  return typeof normalized === 'string' || typeof normalized === 'number'
    ? String(normalized)
    : undefined
}

function positiveInteger(value: unknown, fallback: number) {
  const parsed = Number.parseInt(stringValue(value) ?? '', 10)

  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback
}

function normalize(source: Record<string, unknown>): PaginationQueryParams {
  const requestedLimit = positiveInteger(source.limit, defaults.limit)
  const sort = stringValue(source.sort)
  const search = stringValue(source.search)?.trim()

  return {
    ...defaults,
    page: positiveInteger(source.page, defaults.page),
    limit: limitItems.includes(requestedLimit) ? requestedLimit : defaults.limit,
    sort: sort === '-id' ? '-id' : '+id',
    search: search || undefined,
  }
}

function same(left: PaginationQueryParams, right: PaginationQueryParams) {
  return (
    left.page === right.page &&
    left.limit === right.limit &&
    left.sort === right.sort &&
    left.search === right.search
  )
}

function applyRoute() {
  const current = normalize(themesStore.queryParams ?? defaults)
  const incoming = normalize(route.query as Record<string, unknown>)

  if (same(current, incoming)) return false
  themesStore.queryParams = incoming

  return true
}

function routeQuery(source: Record<string, unknown>) {
  const query = normalize(source)

  return {
    ...(query.page !== defaults.page ? { page: String(query.page) } : {}),
    ...(query.limit !== defaults.limit ? { limit: String(query.limit) } : {}),
    ...(query.sort !== defaults.sort ? { sort: query.sort } : {}),
    ...(query.search ? { search: query.search } : {}),
  }
}

async function fetchThemes() {
  await themesTable.value?.fetchThemes()
}

async function updateQuery(patch: Partial<PaginationQueryParams>) {
  const next = normalize({ ...themesStore.queryParams, ...patch })

  themesStore.queryParams = next

  const query = routeQuery({ ...next })

  if (JSON.stringify(route.query) !== JSON.stringify(query)) await router.replace({ query })
  await nextTick()
  await fetchThemes()
}

async function handleSearch(searchQuery: string) {
  await updateQuery({ search: searchQuery || undefined, page: 1 })
}

async function handleClear() {
  await updateQuery({ ...defaults })
}

async function handleSort(value: unknown) {
  await updateQuery({ sort: String(value), page: 1 })
}

async function handleLimit(value: unknown) {
  await updateQuery({ limit: Number(value), page: 1 })
}

async function handlePage(page: number) {
  await updateQuery({ page })
}

function handleLoaded(count: number) {
  resultCount.value = count
}

async function handleCreateTheme() {
  const requestEpoch = authContextEpoch()
  const { template, responseCode } = await themesStore.fetchTemplate()

  if (requestEpoch !== authContextEpoch()) return

  if (responseCode !== ResponseCode.OK) return
  themeData.value = cloneReactiveData(template ?? {})
  isDialogVisible.value = true
}

async function handleCreate(payload: ThemeData) {
  try {
    const response = await themesStore.create(payload, true)

    if (![ResponseCode.OK, ResponseCode.CREATED].includes(response.responseCode)) return
    await fetchThemes()
  } catch (error) {
    log.error('Failed to create message theme', error)
  }
}

async function handleEdit(themeGUID: string) {
  await router.push({
    name: PageMeta.Themes.Edit.Name as 'setup-emails-themes-edit-guid',
    params: { guid: themeGUID },
  })
}

themesStore.queryParams = { ...defaults }
applyRoute()
isRouteReady.value = true
onMounted(fetchThemes)

watch(
  () => route.query,
  async () => {
    if (!isRouteReady.value || !applyRoute()) return
    await nextTick()
    await fetchThemes()
  },
  { deep: true }
)

const canReadCount = () =>
  useAuthStore()
    .getAbility()
    ?.can(A.READ, S.MESSAGES_THEMES_PREVIEW_ALL, 'paginationMeta.entriesFound') ?? false

onScopeDispose(
  onAuthContextChanged(() => {
    resultCount.value = 0
    isDialogVisible.value = false
    themeData.value = null
  })
)
</script>

<template>
  <main
    class="themes-workspace"
    data-testid="themes-workspace"
  >
    <VCard class="themes-workspace__hero">
      <VCardItem>
        <div class="themes-workspace__hero-content">
          <div>
            <span class="themes-workspace__eyebrow">Message design library</span>
            <VCardTitle class="themes-workspace__title">Themes</VCardTitle>
            <VCardSubtitle class="themes-workspace__subtitle">
              Open a visual system, tune its CSS and body payloads, and keep delivery cohesive.
            </VCardSubtitle>
          </div>
          <div class="themes-workspace__metric">
            <span>Current view</span>
            <strong v-if="canReadCount()">{{ resultCount.toLocaleString() }}</strong>
          </div>
        </div>
      </VCardItem>
      <VDivider />
      <VCardActions class="themes-workspace__actions">
        <VBtn
          color="primary"
          prepend-icon="tabler-plus"
          @click="handleCreateTheme"
        >
          New theme
        </VBtn>
        <VBtn
          prepend-icon="tabler-refresh"
          variant="tonal"
          @click="fetchThemes"
        >
          Refresh
        </VBtn>
      </VCardActions>
    </VCard>
    <ServiceListToolbar
      :query-params="themesStore.queryParams"
      :result-count="canReadCount() ? resultCount : null"
      :sort-items="sortItems"
      heading="Find the next message theme to manage"
      limit-label="Message themes per page"
      plural-label="themes"
      search-label="Search message themes"
      search-placeholder="Name, style, body or account"
      singular-label="theme"
      sort-label="Sort message themes"
      @clear="handleClear"
      @limit-change="handleLimit"
      @refresh="fetchThemes"
      @search="handleSearch"
      @sort-change="handleSort"
    />

    <ThemesDataTable
      ref="themesTable"
      @edit="handleEdit"
      @loaded="handleLoaded"
      @page-change="handlePage"
    />
  </main>

  <ThemeDialog
    v-if="themeData"
    v-model:is-dialog-visible="isDialogVisible"
    :display-type="DialogDisplayType.CREATE"
    :theme-data="themeData"
    @create="handleCreate"
  />
</template>

<style scoped lang="scss">
.themes-workspace {
  display: grid;
  min-inline-size: 0;
  gap: 1.5rem;
  font-feature-settings: 'cv01', 'ss03';
}

.themes-workspace__hero {
  overflow: hidden;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
  background:
    radial-gradient(circle at 88% -30%, rgba(var(--v-theme-primary), 0.2), transparent 28rem),
    linear-gradient(110deg, rgba(var(--v-theme-surface-bright), 0.98), rgb(var(--v-theme-surface)));
  box-shadow: 0 18px 52px rgba(var(--v-theme-on-background), 0.06);
}

.themes-workspace__hero-content {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 2rem;
  padding-block: 0.4rem;
}

.themes-workspace__eyebrow {
  color: rgb(var(--v-theme-primary));
  font-size: 0.6875rem;
  font-weight: 590;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.themes-workspace__title {
  margin-block-start: 0.35rem;
  font-size: clamp(1.75rem, 3vw, 2.35rem);
  font-weight: 590;
  letter-spacing: -0.04em;
}

.themes-workspace__subtitle {
  max-inline-size: 48rem;
  margin-block-start: 0.65rem;
  white-space: normal;
}

.themes-workspace__metric {
  display: grid;
  min-inline-size: 7rem;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  background: rgba(var(--v-theme-surface), 0.7);
  gap: 0.2rem;
  padding: 0.75rem 0.9rem;
}

.themes-workspace__metric span {
  color: rgba(var(--v-theme-on-surface), 0.56);
  font-size: 0.6875rem;
  text-transform: uppercase;
}

.themes-workspace__metric strong {
  font-size: 1.25rem;
  font-weight: 590;
}

.themes-workspace__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.85rem 1rem;
}

@media (max-width: 599px) {
  .themes-workspace {
    gap: 1rem;
  }

  .themes-workspace__hero-content {
    align-items: stretch;
    flex-direction: column;
    gap: 1rem;
  }

  .themes-workspace__metric {
    min-inline-size: 0;
  }

  .themes-workspace__actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .themes-workspace__actions :deep(.v-btn) {
    min-block-size: 3rem;
    inline-size: 100%;
  }
}
</style>
